import { BETWAY_URLS } from "../api/constants";

/**
 * Global utility for handling external URL opening
 * Handles both regular browsers and mobile webviews
 */

/**
 * Gets the country code from Redux store or URL parameters
 * This mirrors the logic used in LocalizationContext
 * @returns {string} Country code
 */
const getCountryCode = () => {
	// Try to get from URL parameters first
	const urlParams = new URLSearchParams(window.location.search);
	const urlCountry = urlParams.get("country");

	// Try to get from Redux store (stored in localStorage by the app)
	let reduxCountry = null;
	try {
		const persistedState = localStorage.getItem("persist:root");
		if (persistedState) {
			const parsed = JSON.parse(persistedState);
			const userState = JSON.parse(parsed.user || "{}");
			reduxCountry = userState.country;
		}
	} catch (e) {
		// Ignore parsing errors
	}

	// Prioritize Redux values, fall back to URL params
	return reduxCountry || urlCountry || null;
};

/**
 * Opens Betway account URL with country-specific configuration
 * No parameters needed - handles everything internally
 */
const openExternalUrl = () => {
	const countryCode = getCountryCode()?.toUpperCase();

	// Determine which Betway configuration to use based on country
	let betwayConfig;
	if (countryCode === "ZA") {
		betwayConfig = BETWAY_URLS.ZA;
	} else if (countryCode === "GH") {
		betwayConfig = BETWAY_URLS.GH;
	} else {
		// Rest of World (RoW) for all other countries
		betwayConfig = BETWAY_URLS.ROW;
	}

	const urlParams = new URLSearchParams(betwayConfig.params);
	const urlWithParams = `${betwayConfig.url}?${urlParams.toString()}`;

	try {
		window.webkit.messageHandlers.openExternal.postMessage({ url: urlWithParams });
	} catch (error) {
		console.warn("Error opening external URL: ", error);
	}
};

/**
 * Opens Betway account URL for Android WebView
 * No parameters needed - handles everything internally
 */
const openExternalUrlAndroid = () => {
	const countryCode = getCountryCode()?.toUpperCase();

	// Determine which Betway configuration to use based on country
	let betwayConfig;
	if (countryCode === "ZA") {
		betwayConfig = BETWAY_URLS.ZA;
	} else if (countryCode === "GH") {
		betwayConfig = BETWAY_URLS.GH;
	} else {
		// Rest of World (RoW) for all other countries
		betwayConfig = BETWAY_URLS.ROW;
	}

	const urlParams = new URLSearchParams(betwayConfig.params);
	const urlWithParams = `${betwayConfig.url}?${urlParams.toString()}`;

	if (window.Android && typeof window.Android.openExternal === "function") {
		window.Android.openExternal(urlWithParams);
	}
};

/**
 * Opens app store URL based on device type
 * @param {string} url - The app store URL to open
 */
const openAppStoreUrl = (url) => {
	try {
		window.webkit.messageHandlers.openExternal.postMessage({ url });
	} catch (error) {
		window.open(url, "_blank", "noopener,noreferrer");
	}
};

/**
 * Opens app store URL for Android WebView
 * @param {string} url - The app store URL to open
 */
const openAppStoreUrlAndroid = (url) => {
	if (window.Android && typeof window.Android.openExternal === "function") {
		window.Android.openExternal(url);
	}
};

// Make them available globally on window object in multiple ways
window.openExternalUrl = openExternalUrl;
window.openAppStoreUrl = openAppStoreUrl;
window.openExternalUrlAndroid = openExternalUrlAndroid;
window.openAppStoreUrlAndroid = openAppStoreUrlAndroid;

// Also try attaching to document for webview compatibility
if (typeof document !== "undefined") {
	document.openExternalUrl = openExternalUrl;
	document.openAppStoreUrl = openAppStoreUrl;
	document.openExternalUrlAndroid = openExternalUrlAndroid;
	document.openAppStoreUrlAndroid = openAppStoreUrlAndroid;
}

// Also try creating a global namespace
window.BetwayUtils = window.BetwayUtils || {};
window.BetwayUtils.openExternalUrl = openExternalUrl;
window.BetwayUtils.openAppStoreUrl = openAppStoreUrl;
window.BetwayUtils.openExternalUrlAndroid = openExternalUrlAndroid;
window.BetwayUtils.openAppStoreUrlAndroid = openAppStoreUrlAndroid;

// Also export for module usage
export { openExternalUrl, openAppStoreUrl, openExternalUrlAndroid, openAppStoreUrlAndroid };
